import * as CONSTANT from '../constants';
import { pulse } from './pulseTracker';

/**
 * Static screen keys for Pulse `view` events.
 * Sent as `view_id` (page_id is campaign URL only).
 * Same strings must be used on native mobile.
 */
export const PULSE_VIEW_ID = {
  HOME: 'home',
  MENU: 'menu',
  /** Any menu category tab (Soups, Salads, …) — not Try 2 / Kids Combo */
  CATEGORY_MENU: 'category_menu',
  PRODUCT: 'item_detail',
  /**
   * Try 2 / Kids Combo landing (menu tab or `/kidsCombo`).
   * Nested category / slot menu uses TRY2COMBO_MENU / KIDSCOMBO_MENU.
   */
  ITEM_DETAIL: 'item_detail',
  TRY2COMBO_MENU: 'try2combo_menu',
  KIDSCOMBO_MENU: 'kidscombo_menu',
  /** @deprecated Prefer ITEM_DETAIL for Kids Combo landing */
  KIDS_COMBO: 'item_detail',
  CHECKOUT: 'checkout',
  THANK_YOU: 'thank_you',
  ORDER_TRACKING_PUBLIC: 'order_tracking_public',
  /** Header track-bar → live order tracking modal */
  ORDER_TRACKING_MODAL: 'order_tracking_modal',
  /** Cancel order confirmation modal (thank you / live tracking) */
  CANCEL_ORDER_MODAL: 'cancel_order_modal',
  SCAN: 'scan',
  /** Scan → Add Points / receipt order-id screen */
  ADD_POINTS: 'add_points',
  /** Rewards / Scan → Get Offer By Code (promo) screen */
  PROMO_CODE: 'promo_code',
  REWARDS: 'rewards',
  REWARDS_OFFERS: 'offers',
  REWARDS_FAQ: 'rewards_faq',
  LOGIN: 'login',
  SIGNUP: 'signup',
  FORGOT_PASSWORD: 'forgot_password',
  NEW_PASSWORD: 'new_password',
  PROFILE_PERSONAL_INFO: 'personal_info',
  PROFILE_PAYMENTS: 'payments',
  PROFILE_GIFT_CARDS: 'gift_cards',
  PROFILE_DELIVERY_ADDRESSES: 'delivery_addresses',
  PROFILE_DEVICES: 'trusted_devices',
  /** Profile → View more devices page */
  ALL_PROFILE_DEVICES: 'all_trusted_devices',
  PROFILE_DELETE_ACCOUNT: 'delete_account',
  ORDERS_RECENT: 'recent_orders',
  ORDERS_FAVORITE: 'favorite_orders',
  PRINT_RECEIPT: 'print_receipt',
  LOCATIONS: 'locations',
  LOCATIONS_ALL: 'store_locator',
  LOCATION_DETAIL: 'location_detail',
  NUTRITION: 'nutrition',
  NUTRITION_CATEGORY_MENU: 'nutrition_category_menu',
  NUTRITION_ITEM: 'nutrition_item_detail',
  OUR_KITCHEN: 'our_kitchen',
  FUNDRAISING: 'fundraising',
  /** Fundraising: request / book fundraiser form modal */
  FUNDRAISER_REQUEST_MODAL: 'fundraiser_request_modal',
  CONTACT: 'contact',
  TERMS: 'terms',
  PRIVACY: 'privacy',
  CAREERS: 'careers',
  CAREERS_VALUES: 'careers_values',
  CAREERS_TRAVEL_ABROAD: 'careers_travel_abroad',
  CAREERS_HOSPITALITY: 'careers_hospitality',
  CAREERS_CULINARY: 'careers_culinary',
  CAREERS_LEADERSHIP: 'careers_leadership',
  CAREERS_CATERING: 'careers_catering',
  KIOSK: 'kiosk',
  MAINTENANCE: 'maintenance',
  /** Order type / location selector (OrderType in LocationModal, cart, etc.) */
  LOCATION_SELECTOR_MODAL: 'location_selector_modal',
  /** Pickup / delivery time slot picker */
  CAPACITY_SELECTOR_MODAL: 'capacity_selector_modal',
  /** Guest login / checkout entry modal */
  GUEST_LOGIN_MODAL: 'guest_login_modal',
  /** Cart modal / sheet */
  CART: 'cart',
  /** Item customization modal (mobile full-screen; also first-time modal) */
  CUSTOMIZATION_MODAL: 'customization_modal',
  /** Item customization inline section (desktop web) */
  CUSTOMIZATION: 'customization',
  /** Other dressings / other required modifiers picker inside customization */
  OTHER_CUSTOMIZATION_MODAL: 'other_customization_modal',
  /** Checkout → Edit Store Location modal */
  EDIT_STORE_LOCATION_MODAL: 'edit_store_location_modal',
  /** Place order → Order Details confirmation modal */
  ORDER_SUMMARY: 'order_summary_modal',
  /** Item added confirmation modal */
  ADDED_TO_CART_MODAL: 'added_to_cart_modal',
  /** Item added confirmation when upsell items are shown */
  ADDED_TO_CART_WITH_UPSELL_MODAL: 'added_to_cart_with_upsell_modal',
  /** Rewards: redeem points item detail modal */
  REDEEM_POINTS_FOR_REWARD_MODAL: 'redeem_points_for_reward_modal',
  /** Rewards: redeem success state in that modal */
  REDEEM_POINTS_FOR_REWARD_SUCCESS_MODAL: 'redeem_points_for_reward_success_modal',
  /** Rewards: owned reward detail modal (My Rewards) */
  REDEEM_REWARD_MODAL: 'redeem_reward_modal',
  /** Rewards: View All redeemed rewards modal */
  ALL_REDEEMED_REWARDS: 'all_redeemed_rewards',
  /** Checkout → credit card modal (Add / Edit button) */
  ADD_CREDIT_CARD_MODAL: 'add_credit_card_modal',
  EDIT_CREDIT_CARD_MODAL: 'edit_credit_card_modal',
  /** Curbside vehicle modal (first-time / pickup flow) */
  ADD_NEW_VEHICLE_MODAL: 'add_new_vehicle_modal',
  SELECT_VEHICLE_MODAL: 'select_vehicle_modal',
  /** Checkout edit → select saved vehicle */
  EDIT_SELECT_VEHICLE_MODAL: 'edit_select_vehicle_modal',
  /** Profile → Edit personal info */
  EDIT_PERSONAL_INFO_MODAL: 'edit_personal_info_modal',
  /** Profile → delivery address form */
  ADD_DELIVERY_ADDRESS_MODAL: 'add_delivery_address_modal',
  EDIT_DELIVERY_ADDRESS_MODAL: 'edit_delivery_address_modal',
  /** Profile → vehicle form */
  ADD_VEHICLE_MODAL: 'add_vehicle_modal',
  EDIT_VEHICLE_MODAL: 'edit_vehicle_modal',
  /** Profile → add payment method (Stripe) */
  ADD_PAYMENT_METHOD_MODAL: 'add_payment_method_modal',
  /** Profile → gift cards */
  ADD_GIFT_CARD_MODAL: 'add_gift_card_modal',
  EDIT_GIFT_CARD_MODAL: 'edit_gift_card_modal',
  GIFT_CARD_LOOKUP_MODAL: 'gift_card_lookup_modal',
  GIFT_CARD_LOOKUP_CONFIRMATION_MODAL: 'gift_card_lookup_confirmation_modal',
  /** Profile → delete account confirm */
  DELETE_ACCOUNT_MODAL: 'delete_account_modal',
  /** Profile → devices */
  LOGOUT_DEVICE_MODAL: 'logout_device_modal',
  LOGOUT_ALL_DEVICES_MODAL: 'logout_all_devices_modal',
  OTP_VERIFICATION_MODAL: 'otp_verification_modal',
  /** Offer failed eligibility criteria (cart / rewards) */
  OFFER_ELIGIBILITY: 'offer_eligibility_modal',
  /** reCAPTCHA challenge (login, forgot password, guest checkout) */
  RECAPTCHA_VERIFICATION: 'recaptcha_verification_modal',
  /** Favorite / reorder (profile + related) */
  NAME_FAVORITE_ORDER_MODAL: 'name_favorite_order_modal',
  REMOVE_FAVORITE_ORDER_MODAL: 'remove_favorite_order_modal',
  CONFIRM_REORDER_MODAL: 'confirm_reorder_modal',
} as const;

export type PulseViewId = (typeof PULSE_VIEW_ID)[keyof typeof PULSE_VIEW_ID] | string;

/** Shared category slug: `Chef's Secret Menu` → `chefs_secret_menu` */
export const toCategorySlug = (categoryName: string): string => {
  let decoded = String(categoryName || '').trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    // keep raw if malformed % sequences
  }
  return decoded
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

/**
 * Menu category tab view_id.
 * All categories share `category_menu` (category name stays in page_path).
 * Try 2 / Kids Combo are handled separately as `item_detail`.
 */
export const toMenuCategoryViewId = (_categoryName?: string): string => {
  return PULSE_VIEW_ID.CATEGORY_MENU;
};

/**
 * Nutrition category page view_id.
 * All categories share `nutrition_category_menu` (name stays in page_path).
 */
export const toNutritionCategoryViewId = (_categoryName?: string): string => {
  return PULSE_VIEW_ID.NUTRITION_CATEGORY_MENU;
};

/** Profile Tab.Container eventKeys (mobile) → view_id */
export const profileTabToViewId = (tabKey: string | null | undefined): string => {
  switch (tabKey) {
    case 'second':
      return PULSE_VIEW_ID.PROFILE_PAYMENTS;
    case 'third':
      return PULSE_VIEW_ID.PROFILE_GIFT_CARDS;
    case 'fourth':
      return PULSE_VIEW_ID.PROFILE_DELIVERY_ADDRESSES;
    case 'eight':
      return PULSE_VIEW_ID.PROFILE_DEVICES;
    case 'nine':
      return PULSE_VIEW_ID.PROFILE_DELETE_ACCOUNT;
    case 'first':
    default:
      return PULSE_VIEW_ID.PROFILE_PERSONAL_INFO;
  }
};

/** Desktop scrollspy section element ids → view_id */
export const PROFILE_SECTION_IDS = [
  'Personal_Info',
  'Payment',
  'Gift_Cards',
  'Delivery_Address',
  'Your_Devices',
  'Delete_Account',
] as const;

export const profileSectionIdToViewId = (
  sectionId: string | null | undefined
): string | null => {
  switch (sectionId) {
    case 'Personal_Info':
      return PULSE_VIEW_ID.PROFILE_PERSONAL_INFO;
    case 'Payment':
      return PULSE_VIEW_ID.PROFILE_PAYMENTS;
    case 'Gift_Cards':
      return PULSE_VIEW_ID.PROFILE_GIFT_CARDS;
    case 'Delivery_Address':
      return PULSE_VIEW_ID.PROFILE_DELIVERY_ADDRESSES;
    case 'Your_Devices':
      return PULSE_VIEW_ID.PROFILE_DEVICES;
    case 'Delete_Account':
      return PULSE_VIEW_ID.PROFILE_DELETE_ACCOUNT;
    default:
      return null;
  }
};

const EXACT_ROUTE_VIEWS: Record<string, string> = {
  [CONSTANT.ROUTE_HOME]: PULSE_VIEW_ID.HOME,
  [CONSTANT.ROUTE_KIDS_COMBO]: PULSE_VIEW_ID.ITEM_DETAIL,
  [CONSTANT.ROUTE_CHECKOUT]: PULSE_VIEW_ID.CHECKOUT,
  [CONSTANT.ROUTE_THANK_YOU]: PULSE_VIEW_ID.THANK_YOU,
  [CONSTANT.ROUTE_SCAN]: PULSE_VIEW_ID.SCAN,
  [CONSTANT.ROUTE_FAQ]: PULSE_VIEW_ID.REWARDS_FAQ,
  [CONSTANT.ROUTE_LOGIN]: PULSE_VIEW_ID.LOGIN,
  [CONSTANT.ROUTE_CREATE_ACCOUNT]: PULSE_VIEW_ID.SIGNUP,
  [CONSTANT.ROUTE_FORGOT_PASSWORD]: PULSE_VIEW_ID.FORGOT_PASSWORD,
  [CONSTANT.ROUTE_NEW_PASSWORD]: PULSE_VIEW_ID.NEW_PASSWORD,
  [CONSTANT.PRINT_RECEIPT]: PULSE_VIEW_ID.PRINT_RECEIPT,
  [CONSTANT.ROUTE_LOCATIONS]: PULSE_VIEW_ID.LOCATIONS,
  [CONSTANT.ROUTE_VIEW_ALL_LOCATIONS]: PULSE_VIEW_ID.LOCATIONS_ALL,
  [CONSTANT.VIEW_MORE_RECENT_ORDER]: PULSE_VIEW_ID.ORDERS_RECENT,
  [CONSTANT.VIEW_MORE_RECENT_FAVORITE_ORDER]: PULSE_VIEW_ID.ORDERS_FAVORITE,
  [CONSTANT.ROUTE_PROFILE_DEVICES]: PULSE_VIEW_ID.ALL_PROFILE_DEVICES,
  [CONSTANT.ROUTE_NUTRITION]: PULSE_VIEW_ID.NUTRITION,
  [CONSTANT.ROUTE_OUR_KITCHEN]: PULSE_VIEW_ID.OUR_KITCHEN,
  [CONSTANT.ROUTE_FUNDRAISING]: PULSE_VIEW_ID.FUNDRAISING,
  [CONSTANT.ROUTE_TALK_TO_US]: PULSE_VIEW_ID.CONTACT,
  [CONSTANT.TERMS_ROUTE]: PULSE_VIEW_ID.TERMS,
  [CONSTANT.TERMS_ROUTE_HTML]: PULSE_VIEW_ID.TERMS,
  [CONSTANT.PRIVACY_POLICY_ROUTE]: PULSE_VIEW_ID.PRIVACY,
  [CONSTANT.ROUTE_CAREERS]: PULSE_VIEW_ID.CAREERS,
  [CONSTANT.ROUTE_CAREERS_VALUES]: PULSE_VIEW_ID.CAREERS_VALUES,
  [CONSTANT.ROUTE_TRAVEL_ABROAD]: PULSE_VIEW_ID.CAREERS_TRAVEL_ABROAD,
  [CONSTANT.ROUTE_HOSPITALITY_TEAM]: PULSE_VIEW_ID.CAREERS_HOSPITALITY,
  [CONSTANT.ROUTE_CULINARY_TEAM]: PULSE_VIEW_ID.CAREERS_CULINARY,
  [CONSTANT.ROUTE_LEADERSHIP_TEAM]: PULSE_VIEW_ID.CAREERS_LEADERSHIP,
  [CONSTANT.ROUTE_CATERING_TEAM]: PULSE_VIEW_ID.CAREERS_CATERING,
  [CONSTANT.CATERING_CAFE_ZUPAS_KIOSK]: PULSE_VIEW_ID.KIOSK,
  [CONSTANT.ROUTE_MAINTENANCE]: PULSE_VIEW_ID.MAINTENANCE,
};

/** Routes behind RequireAuth — skip auto `view` until the user is authenticated. */
const AUTH_GATED_ROUTE_PATHS = new Set<string>([
  CONSTANT.ROUTE_SCAN,
  CONSTANT.ROUTE_PROFILE_DEVICES,
]);

export const normalizePulseRoutePath = (pathname: string): string =>
  !pathname || pathname === '/'
    ? CONSTANT.ROUTE_HOME
    : pathname.replace(/\/+$/, '') || CONSTANT.ROUTE_HOME;

export const isAuthGatedPulseRoute = (pathname: string): boolean =>
  AUTH_GATED_ROUTE_PATHS.has(normalizePulseRoutePath(pathname));

/** Auto-tracked route view_id, or null when skipped (local handler / auth gate). */
export const resolveAutoTrackedPulseViewId = (
  pathname: string,
  isAuthenticated: boolean
): string | null => {
  if (isAuthGatedPulseRoute(pathname) && !isAuthenticated) {
    return null;
  }
  return resolvePhase1RouteViewId(pathname);
};

/**
 * Resolve view_id for routes handled by PulseViewTracker.
 * Returns null for /menu*, /rewards, /profileDetails (local tab handlers).
 */
export const resolvePhase1RouteViewId = (pathname: string): string | null => {
  const path = normalizePulseRoutePath(pathname);

  // Menu tiles + category tabs — local handlers only
  if (path === CONSTANT.ROUTE_MENU || path.startsWith(`${CONSTANT.ROUTE_MENU}/`)) {
    return null;
  }
  // Rewards tabs / desktop rewards — local handlers only
  if (path === CONSTANT.ROUTE_REWARDS_REDEEM) {
    return null;
  }
  // Profile tabs share one route — local handler only
  if (path === CONSTANT.ROUTE_PROFILE_DETAILS) {
    return null;
  }

  if (EXACT_ROUTE_VIEWS[path]) {
    return EXACT_ROUTE_VIEWS[path];
  }

  if (path.startsWith(`${CONSTANT.ROUTE_ORDER}/`)) {
    return PULSE_VIEW_ID.PRODUCT;
  }

  if (path.startsWith(`${CONSTANT.ROUTE_ORDER_TRACKING}/`)) {
    return PULSE_VIEW_ID.ORDER_TRACKING_PUBLIC;
  }

  // Store detail `/location/:id` (avoid matching `/locations…`)
  if (path.startsWith(`${CONSTANT.ROUTE_LOCATION_INFO}/`)) {
    const locationId = path.slice(`${CONSTANT.ROUTE_LOCATION_INFO}/`.length);
    if (locationId && !locationId.includes('/')) {
      return PULSE_VIEW_ID.LOCATION_DETAIL;
    }
  }

  // Nutrition `/nutrition/:segment` — numeric → item; else category slug
  if (path.startsWith(`${CONSTANT.ROUTE_NUTRITION}/`)) {
    const segment = path.slice(`${CONSTANT.ROUTE_NUTRITION}/`.length);
    if (!segment || segment.includes('/')) {
      return null;
    }
    if (/^\d+$/.test(segment)) {
      return PULSE_VIEW_ID.NUTRITION_ITEM;
    }
    return toNutritionCategoryViewId(segment);
  }

  return null;
};

/** Fire a Pulse view with static view_id. */
export const trackPulseView = (
  viewId: string,
  extra: { page_path?: string } = {}
): void => {
  pulse.view({
    view_id: viewId,
    ...(extra.page_path !== undefined ? { page_path: extra.page_path } : {}),
  });
};
